import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowDown, ChevronLeft, ChevronRight } from "lucide-react";

const SLIDES = [
  {
    id: "report",
    eyebrow: "EY Tech LLC — Website Issue Report",
    title: (
      <>
        Flawless digital <em className="text-blue-600">access.</em>
      </>
    ),
    sub: "We read your public pages the way an auditor — and a plaintiff — would. Then we hand you a written report of every problem and exactly what to change.",
    chips: ["$99 once", "3 business days", "You keep the report"],
    ctaLabel: "Get the $99 Report",
    ctaTo: "/report",
    ctaTestId: "hero-cta-report",
  },
  {
    id: "email-check",
    eyebrow: "EY Tech LLC — Email Delivery Check",
    title: (
      <>
        If quotes may be <em className="text-blue-600">landing in spam.</em>
      </>
    ),
    sub: "Gmail and Outlook now bounce or bury mail from companies whose public mail records are incomplete. We check the public records for your domain and send a written report of what is missing and the exact lines to paste.",
    chips: ["$49 once", "1 business day", "Public DNS only"],
    ctaLabel: "Get the $49 Email Check",
    ctaTo: "/email-check",
    ctaTestId: "hero-cta-email-check",
  },
  {
    id: "eu-sell-through",
    eyebrow: "EY Tech LLC — EU Sell-Through Pack",
    title: (
      <>
        Your Amazon EU listing needs an EU Responsible Person.{" "}
        <em className="text-blue-600">We package the file.</em>
      </>
    ),
    sub: "GPSR has applied since 13 Dec 2024. We map your SKUs, prep the Amazon product-safety fields, lay out the label, and match you with an EU-established Responsible Person provider.",
    chips: ["$399 once", "5–7 days", "Remote · English"],
    ctaLabel: "Get the $399 EU Pack",
    ctaTo: "/eu-sell-through",
    ctaTestId: "hero-cta-eu-pack",
  },
];

export const HeroCarousel = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const timer = setInterval(() => setIndex((i) => (i + 1) % SLIDES.length), 6000);
    return () => clearInterval(timer);
  }, [paused]);

  const go = (i) => setIndex((i + SLIDES.length) % SLIDES.length);
  const slide = SLIDES[index];

  return (
    <div
      data-testid="hero-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="min-h-[520px] md:min-h-[440px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={slide.id}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.15}
            onDragEnd={(e, info) => {
              if (info.offset.x < -60) go(index + 1);
              else if (info.offset.x > 60) go(index - 1);
            }}
            className="cursor-grab active:cursor-grabbing"
          >
            <p
              data-testid="hero-eyebrow"
              className="mb-6 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600"
            >
              {slide.eyebrow}
            </p>
            <h1
              data-testid="hero-heading"
              className="max-w-4xl font-serif text-5xl font-medium leading-[1.05] tracking-tight text-slate-900 md:text-7xl lg:text-8xl"
            >
              {slide.title}
            </h1>
            <p
              data-testid="hero-subtext"
              className="mt-8 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg"
            >
              {slide.sub}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {slide.chips.map((chip) => (
                <span
                  key={chip}
                  className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 shadow-soft"
                >
                  {chip}
                </span>
              ))}
            </div>
            <div className="mt-10">
              <Link
                to={slide.ctaTo}
                data-testid={slide.ctaTestId}
                className="group inline-flex items-center gap-2.5 rounded-full bg-blue-600 px-8 py-4 text-sm font-semibold text-white shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-md"
              >
                {slide.ctaLabel}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-12 flex items-center justify-between border-t border-slate-200 pt-8">
        <div className="flex items-center gap-3">
          <button
            data-testid="hero-prev-button"
            onClick={() => go(index - 1)}
            aria-label="Previous service"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors duration-300 hover:border-blue-600 hover:text-blue-600"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            data-testid="hero-next-button"
            onClick={() => go(index + 1)}
            aria-label="Next service"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors duration-300 hover:border-blue-600 hover:text-blue-600"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <div className="ml-3 flex items-center gap-2">
            {SLIDES.map((s, i) => (
              <button
                key={s.id}
                data-testid={`hero-dot-${i}`}
                onClick={() => go(i)}
                aria-label={`Go to ${s.eyebrow}`}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === index ? "w-8 bg-blue-600" : "w-2 bg-slate-300 hover:bg-slate-400"
                }`}
              />
            ))}
          </div>
        </div>
        <span className="flex items-center gap-2 text-sm font-medium text-slate-500">
          <ArrowDown className="h-4 w-4 animate-bounce" />
          Scroll
        </span>
      </div>
    </div>
  );
};
